// Sugalarm — Capacitor surface (cycle 26).
//
// Single owner of `window.Capacitor` access. Every consumer that
// needs to know whether the SPA is running inside the native shell
// (`useFrameVariant`, `env.ts`, `App.tsx`) imports from here so the
// detection lives in one place — same rule as cycle 25's
// `useFrameVariant` ownership of frame-variant detection.
//
// `web/` does not depend on `@capacitor/*` at build time. The plugin
// packages are pulled in lazily via dynamic `import()` from within
// the helpers below, and resolve at runtime against the
// Capacitor-injected `window.Capacitor` global on native devices.
// On every non-Capacitor surface (kiosk, browser, PWA) the detection
// returns `false`, the dynamic imports never execute, and the
// production bundle is unchanged from cycle 25.

/** True when the SPA is running inside the Capacitor native runtime
 *  (iOS / Android). False everywhere else — desktop browser, kiosk
 *  Chromium, mobile Safari, installed PWA. */
export function isCapacitorNative(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as { Capacitor?: { isNativePlatform?: () => boolean } })
    .Capacitor;
  return (
    cap != null &&
    typeof cap.isNativePlatform === "function" &&
    cap.isNativePlatform() === true
  );
}

/** Reports `"ios"` / `"android"` / `"web"` for telemetry / UA strings.
 *  Returns `"web"` whenever `isCapacitorNative()` is false, even on a
 *  mobile Safari / Chrome browser — only the native shell is special. */
export function capacitorPlatform(): "ios" | "android" | "web" {
  if (typeof window === "undefined") return "web";
  const cap = (window as { Capacitor?: { getPlatform?: () => string } })
    .Capacitor;
  if (cap != null && typeof cap.getPlatform === "function") {
    const platform = cap.getPlatform();
    if (platform === "ios" || platform === "android") return platform;
  }
  return "web";
}

/** Append `(Capacitor)` to `navigator.userAgent` so Settings → Devices
 *  can distinguish a native install from a Safari PWA install of the
 *  same user. Returns `navigator.userAgent` unchanged on non-native. */
export function getDeviceUserAgent(version: string): string {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent ?? "" : "";
  if (!isCapacitorNative()) return ua;
  const platform = capacitorPlatform();
  const platformLabel = platform === "ios" ? "iOS" : platform === "android" ? "Android" : "Native";
  const suffix = `Sugalarm-${platformLabel}/${version} (Capacitor)`;
  return ua ? `${ua} ${suffix}` : suffix;
}

/** Idempotent one-shot called from `App.tsx`'s theme bootstrap effect.
 *  Sets the native status bar to light icons on the dark-navy theme
 *  and wires the `appUrlOpen` listener as a no-op so the API surface
 *  exists ahead of cycle 27's APNS deep links. No-ops on every
 *  non-Capacitor surface. */
let chromeInitialized = false;

export async function initCapacitorChrome(): Promise<void> {
  if (!isCapacitorNative()) return;
  if (chromeInitialized) return;
  chromeInitialized = true;
  try {
    // Native plugins are reached via the bridge-injected
    // `window.Capacitor.Plugins` proxies — the pure-remote webview has
    // no bundler, so a bare `import("@capacitor/...")` can't resolve.
    const cap = (
      window as { Capacitor?: { Plugins?: Record<string, unknown> } }
    ).Capacitor;
    const statusBar = cap?.Plugins?.StatusBar as
      | { setStyle: (opts: { style: string }) => Promise<void> }
      | undefined;
    // "DARK" is the wire value of the StatusBar `Style.Dark` enum.
    await statusBar?.setStyle({ style: "DARK" });
  } catch (err) {
    console.warn("[capacitor] status-bar setStyle failed", err);
  }
  // The `appUrlOpen` listener is wired separately in
  // `registerForPushNotifications` (cycle 28) so the deep-link target
  // and the push-tap target share one handler.
}

// ─── Native push (cycle 28) ──────────────────────────────────────────

/** Resolve a natively-registered plugin proxy off the Capacitor bridge.
 *  The SPA is loaded REMOTELY (pure-remote webview, cycle 26) so a bare
 *  dynamic `import("@capacitor/...")` can never resolve at runtime —
 *  there is no bundler in the webview. The bridge instead injects every
 *  natively-installed plugin as a proxy on `window.Capacitor.Plugins`;
 *  that global is the only reliable runtime path to native plugins. */
function capacitorPlugin<T>(name: string): T | null {
  if (!isCapacitorNative()) return null;
  const cap = (
    window as {
      Capacitor?: { Plugins?: Record<string, unknown> };
    }
  ).Capacitor;
  const plugin = cap?.Plugins?.[name];
  return plugin != null ? (plugin as T) : null;
}

/** True iff running inside the OFFICIAL Android kiosk shell — the
 *  device-owner kiosk APK we sell + provision. Cycle 267: this is the
 *  ONLY native signal that grants the full-screen kiosk layout. It is
 *  the native Android platform AND the presence of at least one
 *  kiosk-FLAVOR-only Capacitor plugin (ScreenBrightness c197 / KioskOta
 *  c225 / KioskWifi c224 / Battery c229 / WifiSignal c235.4 — none of
 *  which the `standard` phone flavor, the iOS app, or any browser ever
 *  registers). A random iPad or non-official Android tablet (browser or
 *  store app) therefore never matches, so it keeps the mobile-phone
 *  layout; a full-screen experience requires the provisioned hardware.
 *  Checks several plugins so removing any one later can't silently
 *  demote a live field kiosk to the phone layout. */
export function isNativeKioskShell(): boolean {
  if (capacitorPlatform() !== "android") return false;
  return (
    capacitorPlugin("ScreenBrightness") != null ||
    capacitorPlugin("KioskOta") != null ||
    capacitorPlugin("KioskWifi") != null ||
    capacitorPlugin("Battery") != null ||
    capacitorPlugin("WifiSignal") != null
  );
}

export type NativePushPermission = "granted" | "denied" | "default";

/** One delivered card as the PushNotifications bridge reports it.
 *  `data` is the push's `userInfo` (iOS) — that is where cycle 369's
 *  `kind` / `ts` and cycle 439's `expires_in` live. */
export interface DeliveredNotification {
  id: string;
  data?: Record<string, unknown> | null;
}

interface PushNotificationsProxy {
  requestPermissions: () => Promise<{ receive: string }>;
  register: () => Promise<void>;
  addListener: (
    event: string,
    cb: (data: never) => void,
  ) => Promise<{ remove: () => Promise<void> }>;
  // Cycle 439 — the delivered-card surface, used ONLY by the RECOVERED
  // card sweep. Present since @capacitor/push-notifications v1.
  getDeliveredNotifications: () => Promise<{
    notifications: DeliveredNotification[];
  }>;
  removeDeliveredNotifications: (opts: {
    notifications: DeliveredNotification[];
  }) => Promise<void>;
}

/** Request OS-level permission for push notifications. On a non-native
 *  surface this resolves to `"default"` immediately (and is what
 *  caller code keys off to skip the rest of the handshake). */
export async function requestPushPermission(): Promise<NativePushPermission> {
  if (!isCapacitorNative()) return "default";
  try {
    const push = capacitorPlugin<PushNotificationsProxy>("PushNotifications");
    if (push == null) {
      console.warn(
        "[capacitor] PushNotifications plugin not registered on the bridge",
      );
      return "default";
    }
    const status = await push.requestPermissions();
    if (status.receive === "granted") return "granted";
    if (status.receive === "denied") return "denied";
    return "default";
  } catch (err) {
    console.warn("[capacitor] requestPushPermission failed", err);
    return "default";
  }
}

/** Cycle 439 — read every notification this app still has on the
 *  lock screen / in Notification Center. Empty array off the native
 *  shell, on a bridge without the plugin, or on any failure: a reader
 *  that cannot read must report "nothing", never throw into a caller
 *  whose next move is to DELETE things. */
export async function getDeliveredNotifications(): Promise<
  DeliveredNotification[]
> {
  if (!isCapacitorNative()) return [];
  try {
    const push = capacitorPlugin<PushNotificationsProxy>("PushNotifications");
    const res = await push?.getDeliveredNotifications?.();
    const list = res?.notifications;
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.warn("[capacitor] getDeliveredNotifications failed", err);
    return [];
  }
}

/** Cycle 439 — remove the named delivered notifications. A no-op on an
 *  empty list, so a caller never has to guard the call. */
export async function removeDeliveredNotifications(
  ids: readonly string[],
): Promise<void> {
  if (!isCapacitorNative()) return;
  if (ids.length === 0) return;
  try {
    const push = capacitorPlugin<PushNotificationsProxy>("PushNotifications");
    await push?.removeDeliveredNotifications?.({
      notifications: ids.map((id) => ({ id })),
    });
  } catch (err) {
    console.warn("[capacitor] removeDeliveredNotifications failed", err);
  }
}

export interface RegisterPushOptions {
  /** Called once with the platform-issued token after a successful
   *  `register()`. */
  onToken: (platform: "ios" | "android", token: string) => Promise<void> | void;
  /** Called when the user taps a notification banner (or opens a
   *  `sugalarm://alerts?id=…` URL). */
  onAlertId: (alertId: string) => void;
}

/** Wire the four PushNotifications listeners + the `appUrlOpen`
 *  listener, then call `register()`. Returns an unsubscribe function
 *  that removes every listener.
 *
 *  No-ops with a noop-unsubscribe on non-native surfaces. */
export async function registerForPushNotifications(
  opts: RegisterPushOptions,
): Promise<() => void> {
  if (!isCapacitorNative()) {
    return () => undefined;
  }

  const handles: Array<{ remove: () => Promise<void> }> = [];
  try {
    const push = capacitorPlugin<PushNotificationsProxy>("PushNotifications");
    if (push == null) {
      console.warn(
        "[capacitor] PushNotifications plugin not registered on the bridge",
      );
      return () => undefined;
    }

    handles.push(
      await push.addListener(
        "registration",
        (token: { value: string }) => {
          const platform = capacitorPlatform();
          if (platform !== "ios" && platform !== "android") return;
          // The handler is async-friendly; we don't await it here
          // because the listener signature is sync.
          void opts.onToken(platform, token.value);
        },
      ),
    );
    handles.push(
      await push.addListener(
        "registrationError",
        (error: { error: string }) => {
          console.warn("[capacitor] push registrationError", error?.error);
        },
      ),
    );
    handles.push(
      await push.addListener(
        "pushNotificationReceived",
        () => {
          // Foreground display is the OS's job for cycle 28. An
          // in-app banner is a future cycle. The WebSocket frame is
          // what updates the SPA's reducer in real time.
        },
      ),
    );
    handles.push(
      await push.addListener(
        "pushNotificationActionPerformed",
        (event: {
          notification?: { data?: { alert_id?: string; url?: string } };
        }) => {
          const data = event.notification?.data;
          const alertId = data?.alert_id;
          if (alertId) opts.onAlertId(alertId);
        },
      ),
    );

    await push.register();
  } catch (err) {
    console.warn("[capacitor] registerForPushNotifications failed", err);
  }

  // The cycle-26 `appUrlOpen` no-op moves here so `sugalarm://alerts?id=<uuid>`
  // routes through the same `onAlertId` path as a tap on a push banner.
  try {
    const appProxy = capacitorPlugin<{
      addListener: (
        event: string,
        cb: (event: { url?: string }) => void,
      ) => Promise<{ remove: () => Promise<void> }>;
    }>("App");
    if (appProxy == null) throw new Error("App plugin not on bridge");
    const handle = await appProxy.addListener(
      "appUrlOpen",
      (event: { url?: string }) => {
        const url = event?.url;
        if (!url) return;
        // Accept both `sugalarm://alerts?id=<uuid>` and the https
        // universal-link form `https://sugalarm.fly.dev/alerts?id=<uuid>`.
        try {
          const parsed = new URL(url);
          if (
            parsed.pathname.replace(/\/$/, "").endsWith("/alerts") ||
            parsed.pathname === "/alerts" ||
            parsed.host === "alerts"
          ) {
            const id = parsed.searchParams.get("id");
            if (id) opts.onAlertId(id);
          }
        } catch {
          /* Malformed URL — log and move on. */
          console.warn("[capacitor] unparseable appUrlOpen url", url);
        }
      },
    );
    if (handle && typeof (handle as { remove?: () => Promise<void> }).remove === "function") {
      handles.push(handle as { remove: () => Promise<void> });
    }
  } catch (err) {
    console.warn("[capacitor] appUrlOpen listener failed", err);
  }

  return () => {
    for (const h of handles) {
      void h.remove();
    }
  };
}

/** Cycle 211: wire a standalone `appUrlOpen` listener for the home-screen
 *  widget's `sugalarm://now` deep link → navigate to the Now screen. This
 *  is separate from (and always-on, unlike) the push-flow `appUrlOpen`
 *  listener, which only wires when native push is enabled — a widget tap
 *  must route to Now regardless of push opt-in. It handles ONLY the `now`
 *  target (host `now` or a `/now` path) and ignores everything else, so
 *  it never double-handles the `sugalarm://alerts?id=…` link. No-ops with
 *  a no-op teardown off the native shell. */
export async function wireWidgetDeepLink(
  onNow: () => void,
): Promise<() => void> {
  if (!isCapacitorNative()) return () => undefined;
  try {
    const appProxy = capacitorPlugin<{
      addListener: (
        event: string,
        cb: (event: { url?: string }) => void,
      ) => Promise<{ remove: () => Promise<void> }>;
    }>("App");
    if (appProxy == null) return () => undefined;
    const handle = await appProxy.addListener(
      "appUrlOpen",
      (event: { url?: string }) => {
        const url = event?.url;
        if (!url) return;
        try {
          const parsed = new URL(url);
          if (
            parsed.host === "now" ||
            parsed.pathname.replace(/\/$/, "").endsWith("/now")
          ) {
            onNow();
          }
        } catch {
          /* Malformed URL — ignore (the push listener logs its own). */
        }
      },
    );
    return () => {
      void handle.remove();
    };
  } catch (err) {
    console.warn("[capacitor] widget deep-link listener failed", err);
    return () => undefined;
  }
}
